/**
 * The right slide-over panel.
 *
 * CLAUDE.md §11 gives this rail to the AI Assistant and to contextual insights.
 * It hosts three things, and the third arrived with C12:
 *
 *  - an activity timeline built from the record's own server timestamps;
 *  - the session context an approver needs to know whose eyes they are looking
 *    through;
 *  - **the AI Assistant** (§12), which lives here rather than on a route of its
 *    own because an assistant you have to navigate away to reach has lost the
 *    context it was going to answer about.
 *
 * The Assistant is a MODE rather than a fourth tab appended unconditionally,
 * because it is the only one that is permission-gated: without
 * `ai.assistant.use` — which nobody holds when AI is disabled at the instance,
 * since `ai.Routes()` then mounts nothing — the mode is not offered at all.
 * That is C12 §10.1's "hide, not error", reached by the mechanism every other
 * module's nav already uses rather than by a hardcoded flag.
 *
 * State is module-level so any view can publish context without the shell
 * having to prop-drill a handler down to it.
 */

import { ref, type Ref } from 'vue'

import type { AppIconName } from '@/lib/icons'
import { onSessionEnd } from '@/lib/sessionReset'

export interface TimelineEvent {
  id: string
  label: string
  /** RFC 3339 instant, or a bare business date. Rendered through `EDate`. */
  at: string | null | undefined
  actor?: string
  description?: string
  icon?: AppIconName
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'
}

export interface RailRecord {
  title: string
  subtitle?: string
  /**
   * Instants the record itself carries (created, last seen, last posted). These
   * are facts from the record's own columns, not the audit log.
   */
  events: TimelineEvent[]
  /**
   * The audit-log identity of this record. When both are set the rail fetches
   * `GET /audit/timeline` and renders the real "who did what, when" trail §5
   * requires on every document — which is why a screen only has to say *what*
   * it is showing, never how to look its history up.
   */
  entity?: string
  entityId?: string
  /** Related documents, rendered as a linked-docs chain (§11). */
  links?: { label: string; to: string }[]
}

/**
 * Which face of the rail is showing.
 *
 * `context` is the two contextual things a record publishes; `assistant` is the
 * conversation. The mode is stored rather than derived so that opening the rail
 * from the ⌘K palette's "Ask the Assistant" command lands on the Assistant,
 * while opening it from a document lands on that document's timeline — the same
 * rail, two intents.
 */
export type RailMode = 'context' | 'assistant'

const open = ref(false)
const record = ref<RailRecord | null>(null)
const mode = ref<RailMode>('context')
const assistantSeed = ref('')

/*
 * Module-level means it outlives a sign-out (the page does not reload): the
 * published record carries a document's title, subtitle and audit identity, and
 * the seed is a question someone typed. Neither belongs to the next person on
 * this tab (`lib/sessionReset.ts`).
 */
onSessionEnd(() => {
  open.value = false
  record.value = null
  mode.value = 'context'
  assistantSeed.value = ''
})

export interface RightRail {
  open: Ref<boolean>
  record: Ref<RailRecord | null>
  /** The face currently showing. Never set to `assistant` without the permission. */
  mode: Ref<RailMode>
  /**
   * A question the palette handed over, waiting in the composer.
   *
   * It is SEEDED, never sent: §12 has no path where a keystroke causes a model
   * call the user has not read first, so a ⌘K command fills the box and the
   * user still presses send.
   */
  assistantSeed: Ref<string>
  show: (next: RailRecord) => void
  clear: () => void
  toggle: () => void
  /** Open the rail on the Assistant. The caller has already checked the permission. */
  openAssistant: (seed?: string) => void
}

export function useRightRail(): RightRail {
  return {
    open,
    record,
    mode,
    assistantSeed,
    show: (next) => {
      record.value = next
      // Publishing a record is a contextual act: a document opening the rail
      // must not shove a half-typed conversation out of the way, but it must
      // also not leave the reader looking at the Assistant when they asked for
      // an audit trail. Only an explicitly closed rail switches face.
      if (!open.value) mode.value = 'context'
      open.value = true
    },
    clear: () => {
      record.value = null
    },
    toggle: () => {
      open.value = !open.value
    },
    openAssistant: (seed) => {
      if (seed !== undefined) assistantSeed.value = seed
      mode.value = 'assistant'
      open.value = true
    },
  }
}
